'use client'
import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'

import { BffInterfacesBasket } from '@dfds-pax/booking-experience-bff'
import { SubsidyBreakdown } from '@dfds-pax/shared-components'

interface Props {
  passengers: BffInterfacesBasket.Passenger[]
  outboundPassengerDepartureData: BffInterfacesBasket.PassengerDepartureData[] | undefined
  returnPassengerDepartureData: BffInterfacesBasket.PassengerDepartureData[] | undefined
  currency: string
}

const subsidyTypeToLabel: Record<string, string> = {
  CeutaResidence: 'SUBSIDIES-CEUTA-RESIDENCE',
  GeneralLargeFamily: 'SUBSIDIES-GENERAL-LARGE-FAMILY',
  Military: 'SUBSIDIES-MILITARY',
  SpecialLargeFamily: 'SUBSIDIES-SPECIAL-LARGE-FAMILY',
}

const Passengers: React.FC<Props> = ({
  currency,
  outboundPassengerDepartureData,
  passengers,
  returnPassengerDepartureData,
}): ReactElement => {
  const { t } = useTranslation()

  if (
    outboundPassengerDepartureData &&
    outboundPassengerDepartureData.length > 0 &&
    outboundPassengerDepartureData.some((pd) => {
      const passenger = passengers.find((p) => p.id === pd.passengerId)
      return passenger && passenger.firstName && passenger.lastName
    })
  ) {
    return (
      <>
        {outboundPassengerDepartureData.map((passengerData, index) => {
          const passenger = passengers.find((p) => p.id === passengerData.passengerId)

          if (!passenger) return null

          const returnData = returnPassengerDepartureData?.find((d) => d.passengerId === passengerData.passengerId)

          return (
            <div
              className="typography-body-md-regular flex flex-col"
              data-e2e="passengerSummaryCheckout"
              data-e2e-index={index}
              key={index}
            >
              <span
                className={clsx(
                  'text-semantic-global-text-default flex flex-row items-center capitalize',
                  passengerData.subsidyAmounts.length > 0 ? 'mb-semantic-global-xs' : ''
                )}
              >
                {passenger.firstName} {passenger.lastName}{' '}
                <span className="text-semantic-global-text-light ml-semantic-global-xs typography-body-sm-regular">
                  ({t(passenger?.categoryCode.toUpperCase())})
                </span>
              </span>
              {passengerData.subsidyAmounts.length > 0 || (returnData && returnData.subsidyAmounts.length > 0) ? (
                <SubsidyBreakdown
                  subsidyLegBreakdown={[
                    ...(passengerData.subsidyAmounts.length > 0
                      ? [
                          {
                            isReturnLeg: false,
                            subsidyPrices: passengerData.subsidyAmounts.map((s) => ({
                              amount: s.amount > 0 ? -s.amount : s.amount,
                              currency: currency,
                              label: `${t(subsidyTypeToLabel[s.type] ?? s.type)}${s.type === 'Military' && passengerData.vehicleSubsidyApplied ? ` ${t('SUBSIDIES-MILITARY-VEHICLE-INCLUDED')}` : ''}`,
                            })),
                            title: t('OUTBOUND-SUBSIDIES'),
                          },
                        ]
                      : []),
                    ...(returnData && returnData.subsidyAmounts.length > 0
                      ? [
                          {
                            isReturnLeg: true,
                            subsidyPrices: returnData.subsidyAmounts.map((s) => ({
                              amount: -s.amount,
                              currency: currency,
                              label: `${t(subsidyTypeToLabel[s.type] ?? s.type)}${s.type === 'Military' && returnData.vehicleSubsidyApplied ? ` ${t('SUBSIDIES-MILITARY-VEHICLE-INCLUDED')}` : ''}`,
                            })),
                            title: t('RETURN-SUBSIDIES'),
                          },
                        ]
                      : []),
                  ]}
                />
              ) : null}
            </div>
          )
        })}
      </>
    )
  }

  if (passengers?.length > 0 && passengers.some((p) => p.firstName && p.lastName)) {
    return (
      <div className="gap-semantic-global-2xs typography-body-md-regular flex flex-col" data-e2e="passengerSummaryCheckout">
        {passengers.map(
          (passenger, index) =>
            passenger.firstName && (
              <div className="flex flex-row flex-wrap items-center gap-x-2 text-left" key={index}>
                <span className="text-semantic-global-text-default capitalize">
                  {passenger.firstName} {passenger.lastName}
                </span>
                <span className="text-semantic-global-text-light">({t(passenger?.categoryCode.toUpperCase())})</span>
              </div>
            )
        )}
      </div>
    )
  }

  return (
    <span className="typography-body-md-regular text-semantic-global-text-light lg:typography-body-md-regular text-left">
      {t('MISSING-PASSENGER-INFORMATION')}
    </span>
  )
}

export default Passengers
